<template>
    <div class="container-fluid wrapper pl-4">
        <loader :loader="isLoading" v-if="isLoading" />
        <b-row v-else class="mt-4 mb-4" align-h="between">
            <b-col xl="4" md="12" lg="4" style="padding-left: 40px">
                <label style="width: 100%" class="label-text mb-0">
                    {{ $t('moodPulse.report.questionLabel') }}
                </label>
                <vue-multiselect
                    label="label"
                    track-by="label"
                    style="max-width: 400px"
                    v-model="question"
                    :options="questionsList"
                    :multiple="false"
                    :placeholder="$t('form.placeholder.select')"
                >
                    <template slot="singleLabel" slot-scope="{ option }">
                        <b-badge class="pl-2 pr-2" style="height: 24px; padding-top: 5px" variant="primary">
                            {{ option.label }}
                        </b-badge>
                    </template>
                    <template slot="noOptions">
                        {{ $t('moodPulse.report.noOptionsFound') }}
                    </template>
                </vue-multiselect>
            </b-col>
            <b-col xl="2" md="12" lg="1"></b-col>
            <b-col align-self="center" xl="6" md="12" lg="7">
                <b-row>
                    <b-col>
                        <b-row style="width: 45vw">
                            <v-date-picker
                                style="margin-left: auto; margin-right: 16px; right: 0; position: relative"
                                v-model="range"
                                mode="date"
                                is-range
                                :locale="$i18n.locale"
                            >
                                <template v-slot="{ inputValue, inputEvents }">
                                    <b-row>
                                        <b-col cols="1">
                                            <img
                                                style="margin-left: -15px; padding-top: 4px"
                                                src="@/assets/images/mood-pulse/mdi_calendar.png"
                                            />
                                        </b-col>
                                        <b-form-input
                                            :placeholder="$t('moodPulse.report.initialDatePlaceholder')"
                                            style="width: 120px; margin-right: 10px"
                                            :value="inputValue.start"
                                            v-on="inputEvents.start"
                                            @change="range.start = inputValue.start"
                                        />
                                        <b-form-input
                                            :placeholder="$t('moodPulse.report.finalDatePlaceholder')"
                                            style="width: 120px; margin-right: 0px"
                                            :value="inputValue.end"
                                            v-on="inputEvents.end"
                                            @change="range.end = inputValue.end"
                                        />
                                    </b-row>
                                </template>
                            </v-date-picker>
                            <template>
                                <b-button
                                    style="margin-right: 10px; position: relative"
                                    variant="outline-primary"
                                    @click="exportModal = true"
                                >
                                    {{ $t('moodPulse.report.export') }}
                                </b-button>
                                <BaseFilters :useDefaultSearchButton="true" @search="filterBySection">
                                    <Departments enable-select-all showLabel :value.sync="sections" />
                                </BaseFilters>
                            </template>
                        </b-row>
                    </b-col>
                </b-row>
            </b-col>
        </b-row>
        <b-row class="pl-3" align-h="center">
            <b-col class="vertical-divider" cols="6">
                <apexchart type="bar" width="90%" :options="perWeekOptions" :series="perWeekSeries"></apexchart>
            </b-col>

            <b-col style="width: 350px">
                <b-row>
                    <b-col cols="1">
                        <b-row align-h="end" align-v="end">
                            <b-col class="pr-0" cols="12">
                                <img
                                    class="ml-4 graph-emoji"
                                    style="max-width: 30px; margin-top: 8.7vh"
                                    src="@/assets/images/mood-pulse/emoji/very-happy.png"
                                />
                            </b-col>
                            <b-col class="pr-0" cols="12">
                                <img
                                    class="ml-4 graph-emoji"
                                    style="max-width: 30px; margin-top: 4vh"
                                    src="@/assets/images/mood-pulse/emoji/happy.png"
                                />
                            </b-col>
                            <b-col class="pr-0" cols="12">
                                <img
                                    class="ml-4 graph-emoji"
                                    style="max-width: 30px; margin-top: 4vh"
                                    src="@/assets/images/mood-pulse/emoji/neutral.png"
                                />
                            </b-col>
                            <b-col class="pr-0" cols="12">
                                <img
                                    class="ml-4 graph-emoji"
                                    style="max-width: 30px; margin-top: 4vh"
                                    src="@/assets/images/mood-pulse/emoji/sad.png"
                                />
                            </b-col>
                            <b-col class="pr-0" cols="12">
                                <img
                                    class="ml-4 graph-emoji"
                                    style="max-width: 30px; margin-top: 4vh"
                                    src="@/assets/images/mood-pulse/emoji/very-sad.png"
                                />
                            </b-col>
                        </b-row>
                    </b-col>
                    <b-col cols="11">
                        <apexchart width="90%" type="line" :options="perDayOptions" :series="perDaySeries"></apexchart>
                    </b-col>
                </b-row>
            </b-col>
        </b-row>
        <b-row class="mt-5 mb-3" align-h="center">
            <div class="top-divider" />
            <b-col cols="6">
                <b-row align-h="between">
                    <b-col cols="3">
                        <p class="info-title mb-0">{{ totalUsersToday }}</p>
                        <p class="info-text mb-0">
                            {{
                                totalUsers > 1
                                    ? $t('moodPulse.report.totalAnswersToday')
                                    : $t('moodPulse.report.oneAnswerToday')
                            }}
                        </p>
                    </b-col>
                    <b-col cols="3">
                        <p class="info-title mb-0">{{ totalUsersThisWeek }}</p>
                        <p class="info-text mb-0">
                            {{
                                totalUsers > 1
                                    ? $t('moodPulse.report.totalAnswersThisWeek')
                                    : $t('moodPulse.report.oneAnswerThisWeek')
                            }}
                            <PopoverHelp
                                v-if="showHelp"
                                :id="popoverHelp"
                                :html="[this.$t('popoverTotalAnswerThisWeek')]"
                            />
                        </p>
                    </b-col>
                    <b-col cols="3">
                        <p class="info-title mb-0">{{ totalUsers }}</p>
                        <p class="info-text mb-0">
                            {{
                                totalUsers > 1 ? $t('moodPulse.report.totalAnswers') : $t('moodPulse.report.oneAnswer')
                            }}
                        </p>
                    </b-col>
                    <b-col cols="3">
                        <p class="info-title pl-2 mb-0">{{ percentUsers }}%</p>
                        <p class="info-text mb-0">
                            {{ $t('moodPulse.report.totalAnswersPercent') }}
                        </p>
                    </b-col>
                </b-row>
            </b-col>
            <div class="bottom-divider" />
        </b-row>
        <b-row align-h="center">
            <b-col cols="11">
                <span class="graph-title">
                    {{ $t('moodPulse.report.answerList') }}
                </span>
            </b-col>
            <b-col cols="11" class="mt-1">
                <b-list-group v-if="answers.length" class="awnsers-wrapp mt-2 mb-5">
                    <b-list-group-item
                        style="background-color: #f9f9f9; border: none"
                        class="d-flex align-items-center"
                        v-for="comment in answers"
                        :key="comment.id"
                    >
                        <div
                            @mouseenter="mouseOn(comment.id)"
                            @mouseleave="mouseOff"
                            class="mr-3"
                            style="width: 70px; position: relative"
                        >
                            <avatar
                                :src="comment.anonymous || !comment.user_picture ? comment.emoji : comment.user_picture"
                                :size="70"
                            />
                            <img
                                v-if="!comment.anonymous && comment.user_picture"
                                class="emoji-badge"
                                :src="comment.emoji"
                            />
                            <div v-if="userTip && commentSelected === comment.id" class="user-tooltip">
                                {{
                                    comment.anonymous || !comment.user_name
                                        ? $t('moodPulse.report.userAnonymous')
                                        : comment.user_name
                                }}
                            </div>
                        </div>

                        <b-form-textarea
                            size="lg"
                            rows="3"
                            no-resize
                            readonly
                            :style="comment.comment ? { background: '#f9f9f9' } : { background: '#e4e7ea' }"
                            :value="comment.comment || $t('moodPulse.report.emptyUserComment')"
                        />
                    </b-list-group-item>
                    <b-pagination
                        v-model="currentPage"
                        :total-rows="pageTotal"
                        per-page="3"
                        @input="getAnswers"
                        size="sm"
                        class="mt-3 ml-2"
                        v-if="answers.length"
                    />
                </b-list-group>
                <div class="awnsers-wrapp mt-2 mb-5" style="background-color: #f9f9f9" v-else>
                    <div class="p-5">
                        <p class="info-text text-center">
                            {{ $t('moodPulse.report.emptyComment') }}
                        </p>
                        <div class="row justify-content-center">
                            <div class="col-6">
                                <img class="mt-5 ml-5" src="@/assets/images/icon-enps.svg" />
                            </div>
                        </div>
                    </div>
                </div>
            </b-col>
        </b-row>

        <export-modal @click="(show) => (exportModal = show)" :show="exportModal" :quiz-id="questionId" />
    </div>
</template>

<script>
import { setupCalendar, DatePicker as VDatePicker } from 'v-calendar'
import Avatar from 'vue-avatar'
import VueMultiselect from 'vue-multiselect'
import { getAnswersList, getReportList, getAllQuizes } from '../api'
import BaseFilters from '@/views/components/filters'
import { Departments } from '@/views/components/fields'
import { ExportModal, PopoverHelp } from './report-modal'
import Loader from '@/views/components/loader'
import { graphTitle, getEmojiChoice, getEmojiPerAverage, getTextPerDay } from '../constants'
import i18n from '@/i18n'
import moment from 'moment'

setupCalendar({
    locale: i18n.locale,
    navVisibility: 'focus',
    popoverDirection: 'bottom',
    popoverVisibility: 'focus',
    popoverAlign: 'right',
})

export default {
    components: {
        VDatePicker,
        VueMultiselect,
        Avatar,
        Loader,
        BaseFilters,
        ExportModal,
        Departments,
        PopoverHelp,
    },
    props: {
        showHelp: {
            type: Boolean,
            required: false,
            default: true,
        },
    },
    data() {
        return {
            name: 'QuizReport',
            isLoading: false,
            userTip: false,
            commentSelected: null,
            sectionFilterModal: false,
            exportModal: false,
            question: null,
            range: {
                start: null,
                end: null,
            },
            questionId: null,
            questionsList: [],
            answers: [],
            pageTotal: 1,
            currentPage: 1,
            sections: [],
            percentUsers: 0,
            totalUsers: 0,
            perDayOptions: {
                chart: {
                    zoom: {
                        enabled: false,
                    },
                    toolbar: {
                        show: false,
                    },
                    type: 'line',
                    width: '100%',
                    //height: 450,
                },
                xaxis: {
                    categories: [],
                },
                yaxis: {
                    tickAmount: 1,
                    min: 1,
                    max: 5,
                    show: false,
                },
                tooltip: {
                    y: {
                        formatter: (value) => {
                            return `${value} / ${getTextPerDay(value)}`
                        },
                        title: {
                            formatter: () => 'Média do Humor:',
                        },
                    },
                },
                title: {
                    text: this.$t('moodPulse.report.perDay'),
                    align: 'left',
                    style: graphTitle,
                },
                dataLabels: {
                    enabled: false,
                },
                annotations: { points: [] },
            },
            perDaySeries: [
                {
                    name: 'choices',
                    data: [],
                },
            ],
            perWeekSeries: [],
            perWeekOptions: {
                chart: {
                    type: 'bar',
                    height: 350,
                    stacked: true,
                    stackType: '100%',
                    toolbar: {
                        show: false,
                    },
                },
                tooltip: {
                    y: {
                        formatter: (value) => {
                            return `${value} resposta(s)`
                        },
                    },
                },
                plotOptions: {
                    bar: {
                        horizontal: true,
                    },
                },
                stroke: {
                    width: 1,
                    colors: ['#fff'],
                },
                title: {
                    text: this.$t('moodPulse.report.perWeek'),
                    style: graphTitle,
                },
                xaxis: {
                    categories: [],
                    show: false,
                },
                fill: {
                    opacity: 1,
                },
                colors: ['#DE5B5B', '#FF7941', '#FFAF26', '#5DC974', '#19B5A8'],
                legend: {
                    position: 'bottom',
                    horizontalAlign: 'left',
                    offsetX: 40,
                },
            },
        }
    },
    computed: {
        hasQuiz() {
            return this.questionsList.length > 0
        },
        id() {
            return 'show-help'
        },
    },
    watch: {
        range() {
            if (this.hasQuiz) {
                this.getAnswers()
                this.getReport()
            }
        },
        question() {
            this.questionId = this.question.value
            this.perDayOptions.annotations.points = []
            this.getAnswers()
            this.getReport()
        },
    },
    methods: {
        mouseOn(id) {
            this.userTip = true
            this.commentSelected = id
        },
        mouseOff() {
            this.userTip = false
            this.commentSelected = null
        },
        async getQuizes() {
            this.isLoading = true
            await getAllQuizes()
                .then((data) => {
                    data.forEach((item) => {
                        if (!this.questionId) this.questionId = item.quiz_question[0].quiz_quiz_id

                        let option = {
                            value: item.quiz_question[0].quiz_quiz_id,
                            label: item.quiz_question[0].question,
                            color: '#022B4F',
                        }

                        this.questionsList.push(option)

                        if (item.quiz_question[0].quiz_quiz_id === this.questionId) this.question = option
                    })
                })
                .finally(() => (this.isLoading = false))
        },
        getReport() {
            this.isLoading = true

            let end = this.range.end ? moment(this.range.end).format('YYYY-MM-DD') : moment().format('YYYY-MM-DD')

            getReportList(this.questionId, end)
                .then((reports) => {
                    this.totalUsers = reports.total_users_responded
                    this.totalUsersToday = reports.total_users_responded_today
                    this.totalUsersThisWeek = reports.total_users_responded_this_week
                    this.percentUsers = reports.percent_users_responded
                    let dayXaxis = reports.humor_per_day.map(({ day }) => moment(day).format('DD/MM'))

                    this.perWeekOptions = {
                        ...this.perWeekOptions,
                        xaxis: {
                            categories: reports.humor_per_week.categories,
                            show: false,
                        },
                    }

                    this.perWeekSeries = reports.humor_per_week.series

                    this.perDayOptions = {
                        ...this.perDayOptions,
                        xaxis: {
                            categories: dayXaxis,
                        },
                    }

                    this.perDaySeries[0].data = reports.humor_per_day.map(({ average }) => average)

                    this.perDaySeries[0].data.forEach((average, index) => {
                        let emoji = getEmojiPerAverage(average)

                        let newPoint = {
                            x: this.perDayOptions.xaxis.categories[index],
                            y: average,
                            marker: {
                                size: 0,
                            },
                            image: {
                                path: emoji,
                                offsetY: 9,
                                width: 25,
                                height: 25,
                            },
                        }

                        this.perDayOptions.annotations.points.push(newPoint)
                    })
                })
                .finally(() => (this.isLoading = false))
        },
        getAnswers() {
            this.isLoading = true

            let start = this.range.start && moment(this.range.start).format('YYYY-MM-DD')

            let end = this.range.end && moment(this.range.end).format('YYYY-MM-DD')

            let sections = this.sections.filter((section) => section !== undefined)
            let dataSections = sections.map((section) => section && section.value)

            getAnswersList(this.questionId, this.currentPage, start, end, dataSections)
                .then((answerList) => {
                    this.pageTotal = answerList.total

                    if (answerList.data && answerList.data.length) {
                        let list = answerList.data

                        list.map((comment) => {
                            comment.emoji = getEmojiChoice(comment.quiz_choice_id)
                        })

                        this.answers = list
                    } else this.answers = []
                })
                .finally(() => (this.isLoading = false))
        },
        filterBySection() {
            if (this.hasQuiz) {
                this.getAnswers()
            }
        },
    },
    beforeMount() {
        this.getQuizes().then(() => {
            if (this.hasQuiz) {
                this.getAnswers()
                this.getReport()
            }
        })
    },
    created() {
        this.questionId = this.$route.params.id && parseInt(this.$route.params.id)
    },
    i18n: {
        messages: {
            'en-US': {
                popoverTotalAnswerThisWeek:
                    'By default, the number of responses in the current week is calculated.<br/>' +
                    'When the time interval is filtered, the number of responses for the last week of the filter is calculated.',
            },
            'es-ES': {
                popoverTotalAnswerThisWeek:
                    'De forma predeterminada, se calcula el número de respuestas de la semana actual.<br/>' +
                    'Cuando se filtra el intervalo de tiempo, se calcula el número de respuestas de la última semana del filtro.',
            },
            'pt-BR': {
                popoverTotalAnswerThisWeek:
                    'Por padrão é calculado o número de respostas na semana atual.<br/>' +
                    'Quando o intervalo de tempo é filtrado é calculado o número de respostas referente a última semana do filtro.',
            },
        },
    },
}
</script>

<style scoped>
.awnsers-wrapp {
    background: #f9f9f9;
    border-radius: 5px;
}

.info-text {
    font-family: Roboto !important;
    font-style: normal !important;
    font-weight: 500 !important;
    font-size: 24px !important;
    line-height: 28px !important;
    text-align: center !important;
    color: #3b3f4e !important;
}

.user-tooltip {
    min-width: 170px;
    height: 35px;
    background: #f9f9f9;
    border: 1px solid #022b4f;
    box-sizing: border-box;
    border-radius: 5px;
    font-family: Roboto;
    font-style: normal;
    font-weight: 500;
    font-size: 14px;
    line-height: 16px;
    color: #022b4f;
    position: absolute;
    bottom: 0;
    left: -50px;
    text-align: center;
    padding-top: 9px;
    box-shadow: 0 0 0.7em gray;
}

.emoji-badge {
    border-radius: 50%;
    height: 23px;
    position: absolute;
    top: 0;
    right: 0;
}

.graph-title {
    font-family: Roboto;
    font-style: normal;
    font-weight: 500;
    font-size: 18px;
    line-height: 21px;
    color: #6e6e6e;
}

.vertical-divider {
    border-right: 1px solid #bec0c0 !important;
}

.top-divider {
    border-top: 1px solid #bec0c0 !important;
    height: 20px;
    width: 92%;
    margin-right: 30px;
}

.bottom-divider {
    border-bottom: 1px solid #bec0c0 !important;
    height: 20px;
    width: 92%;
    margin-right: 30px;
}

.info-title {
    font-family: Roboto;
    font-style: normal;
    font-weight: bold;
    font-size: 40px;
    line-height: 47px;
    text-align: center;
    color: #3b3f4e;
}

.wrapper {
    width: 95vw !important;
    background-color: white !important;
    border: 1px solid #bec0c0 !important;
    box-sizing: border-box !important;
    border-radius: 5px !important;
    height: auto !important;
    padding-left: 0;
    padding-right: 0;
    margin-top: 20px;
    display: block !important;
}

.label-text {
    font-family: Roboto;
    font-style: normal;
    font-weight: 500;
    font-size: 16px;
    color: #3b3f4e;
}

.color-tag {
    background-color: #022b4f !important;
    color: white !important;
    height: 20px !important;
}

@media (min-width: 1567px) {
    .graph-emoji {
        width: 30px;
        margin-top: 85%;
    }
}

@media (max-width: 1566px) and (min-width: 1455px) {
    .graph-emoji {
        margin-top: 90%;
        width: 25px;
    }
}

@media (max-width: 1454px) and (min-width: 1283px) {
    .graph-emoji {
        width: 25px;
        margin-top: 85%;
    }
}

@media (max-width: 1282px) and (min-width: 1197) {
    .graph-emoji {
        width: 25px;
        margin-top: 80%;
    }
}

@media (max-width: 1196px) {
    .graph-emoji {
        width: 20px;
        margin-top: 90%;
    }
}
</style>
